// Foyer glue, not a port. Routes a packaged-FILE video item to Foyer's own
// native AVPlay path (`AvPlayLayer.tsx`, TIZEN-16) instead of the ported
// engine's Shaka/HTML5 players - entirely OUTSIDE `signage-engine/`, per the
// port plan's own §5: "SignageEngineSurface... decides up front... whether
// an item routes to Foyer's existing AvPlayLayer... or the ported manifest
// path."
//
// ## Why this bypasses the ported engine entirely, same reasoning as
// ## NativeFallbackLayer
//
// `AvPlayLayer` has no `onPlaybackComplete`/advance-event of any kind
// (confirmed by reading its own prop list) - Foyer's existing renderer
// drives it purely from `playheadAt`'s own wall-clock resolution: the
// CALLER re-renders on a clock tick, `playheadAt` names a (possibly new)
// current item, and `AvPlayLayer`'s `key={item.id}` remounts it when that
// changes. That is a fundamentally different advancement model from the
// ported machine, which drives its OWN `currentIndex` off explicit
// `ASSET_COMPLETE` events. There is no sane way to feed one into the other,
// so - exactly like `NativeFallbackLayer` for template/url - this item
// bypasses the ported engine's state entirely, driven by the SAME
// `playheadAt` result `SignageEngineSurface` already computes for the
// template/url router branch.
//
// ## Why "provenance, not the file extension"
//
// Ruled by Sigge 2026-08-31 (`isAbrItem`'s own doc comment, reused
// unchanged here, not re-derived): a customer's own upload is a progressive
// file, sent to AVPlay; Nenda/rights-holder material is packaged ABR, sent
// to Shaka. A `<video src>` on the Samsung panels this fleet ships fetches
// a progressive file and then declines to decode it - measured on
// hardware, see `avplay.ts`'s own header - so this routing is a real
// hardware-correctness requirement, not a preference.
//
// ## Deferred, not dropped
//
// PLAYER-6/Verimatrix DRM (`drm` prop) is not wired - matches
// `VideoPlayer.tsx`'s own deferred-prop precedent for the ported path. A
// customer's own progressive upload (the only thing routed here at all,
// per "provenance" above) is never rights-managed catalogue material in
// this codebase, so this gap is expected to stay unreachable rather than
// eventually needing to close, unlike the dead-item gap below.
//
// A failed/dead item WAS not tracked or excluded from future rotation here
// - `onFailed` only logged. Fixed: `dead`/`onItemFailed` mirror
// `SignageEngineSurface.tsx`'s own `dead`/`markDead` (itself a direct reuse
// of `SignageSurface.tsx`'s CONTENT-29 fix - the exact bug class this whole
// port exists to fix, "Samsung signage stuck on item 1/75"). The item this
// component is HANDED as `head.item` is never itself re-checked against
// `dead` - the caller (`SignageEngineSurface`) has already walked past any
// dead item with `pickPlayable` before constructing a head at all; this
// component only needs `dead` for its OWN "upcoming" pre-roll pick.
import { useState } from "react";
import type { SignageItem } from "@shared/types/signage";
import type { SignagePlayhead } from "@shared/signage";
import AvPlayLayer, { AvPlaySurface } from "./AvPlayLayer";
import { nextPlayable } from "./SignageSurface";

interface AvPlayFallbackLayerProps {
  head: SignagePlayhead & { item: SignageItem };
  soundEnabled: boolean;
  /** Ids currently excluded from selection - see this file's own header
   *  comment. Used only for the "upcoming" pre-roll pick; `head.item`
   *  itself is trusted as already-live. */
  dead: Set<string>;
  /** Reports a failed item up so the caller can exclude it from future
   *  routing/selection (`SignageEngineSurface.tsx`'s own `markDead`) -
   *  previously logged only, never fed back into selection. */
  onItemFailed: (id: string) => void;
}

export default function AvPlayFallbackLayer({
  head,
  soundEnabled,
  dead,
  onItemFailed,
}: AvPlayFallbackLayerProps) {
  const [mediaState, setMediaState] = useState<string | null>(null);
  const item = head.item;
  const items = head.playlist.items;
  const upcoming = nextPlayable(items, head.index, dead) ?? undefined;
  // A solo playlist never advances: `playheadAt` is a modulo, so it keeps
  // returning this same item and nothing else will ever restart it - same
  // rule SignageSurface.tsx's own AVPlay branch uses. Also true, via
  // `nextPlayable`, when every OTHER item in the playlist is currently dead
  // - there is genuinely nothing else to pre-roll either way.
  const solo = !upcoming || upcoming.id === item.id;
  const muted = item.muted || !soundEnabled;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "#000" }}
      data-testid="avplay-fallback-layer"
    >
      <AvPlaySurface />
      <AvPlayLayer
        key={item.id}
        src={item.assetUrl}
        nextSrc={solo ? undefined : upcoming?.assetUrl}
        muted={muted}
        loop={solo}
        isDash={false}
        onFailed={() => {
          console.error(
            `[signage-engine] AVPlay could not play item ${item.id} (${item.assetUrl})`
          );
          onItemFailed(item.id);
        }}
        onMedia={setMediaState}
      />
      {mediaState && (
        <div
          data-testid="avplay-media-stall"
          style={{ position: "fixed", bottom: 0, left: 0, color: "#f88" }}
        >
          {mediaState}
        </div>
      )}
    </div>
  );
}
